import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { DepartureTimeEnum, DiningFilters, IFilter } from '@shared/models/filter.model';
import { InternationalRegion } from '@shared/models/international-region.model';
import { PaginatedApiResponse } from '@shared/models/paginated-api-response';
import { IFilterTags } from '@shared/models/tag';
import { ApiEndpoint } from '@shared/shared/enums/api-endpoints.enum';
import { TravelCarHireFilterForm } from '@shared/shared/interfaces/travel-car-hire.interfaces';
import { LanguageService } from '@shared/shared/services/language-service';
import { getFilteredCarResult } from '@shared/store/travel-search/reducers/car-hire-search.reducers';
import { capitalize } from '@shared/utils/name.lib';
import { replaceTemplateKeys } from '@shared/utils/url.lib';
import { combineLatest, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import * as FilterActions from '../actions/filter.actions';

@Injectable()
export class FilterEffects {
  constructor(
    private actions$: Actions,
    private http: HttpClient,
    private translationService: TranslateService,
    private languageService: LanguageService
  ) {}

  public getDiningFilters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterActions.GET_DINING_FILTERS),
      switchMap(() => {
        const categoriesUrl = replaceTemplateKeys(ApiEndpoint.DINING_CATEGORIES, {
          locale: this.languageService.currentLanguage,
        });

        const esgCategoriesUrl = replaceTemplateKeys(ApiEndpoint.ESG_DINING_CATEGORIES, {
          locale: this.languageService.currentLanguage,
        });

        const cuisineUrl = replaceTemplateKeys(ApiEndpoint.DINING_CUISINES, {
          locale: this.languageService.currentLanguage,
        });

        const memberBenefitsUrl = replaceTemplateKeys(ApiEndpoint.DINING_MEMBER_BENEFITS, {
          locale: this.languageService.currentLanguage,
        });

        const categoryResults$ = this.http
          .get<{ dining_categories: DiningFilters[] }>(categoriesUrl)
          .pipe(catchError(() => of([])));
        const esgCategoryResults$ = this.http
          .get<{ dining_categories: DiningFilters[] }>(esgCategoriesUrl)
          .pipe(catchError(() => of([])));
        const cuisineResults$ = this.http
          .get<{ dining_cuisines: DiningFilters[] }>(cuisineUrl)
          .pipe(catchError(() => of([])));
        const memberBenefitsResults$ = this.http
          .get<{ dining_member_benefits: DiningFilters[] }>(memberBenefitsUrl)
          .pipe(catchError(() => of([])));

        return combineLatest([cuisineResults$, categoryResults$, esgCategoryResults$, memberBenefitsResults$]).pipe(
          distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
          map(([cuisines, categories, esgCategories, memberBenefits]) => {
            return new FilterActions.GetDiningFiltersSuccess({
              cuisines: cuisines['dining_cuisines'] ? cuisines['dining_cuisines'] : [],
              tags: categories['dining_categories'] ? categories['dining_categories'] : [],
              memberBenefits: memberBenefits['dining_member_benefits'] ? memberBenefits['dining_member_benefits'] : [],
              esg_tags: esgCategories['esg_dining_categories'] ? esgCategories['esg_dining_categories'] : [],
            });
          })
        );
      })
    )
  );

  public getMemberEventFilters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterActions.GET_MEMBER_EVENT_FILTERS),
      switchMap(() => {
        let tagParams = new HttpParams();
        tagParams = tagParams.append('module', 'member_events');
        tagParams = tagParams.append('tag_group', 'category');
        tagParams = tagParams.append('page_size', '200');

        return this.http.get(ApiEndpoint.TAGS, { params: tagParams }).pipe(
          map((result: PaginatedApiResponse<IFilterTags>) => {
            return new FilterActions.GetMemberEventFiltersSuccess(result['results']);
          })
        );
      })
    )
  );

  public getEntertainmentFilters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterActions.GET_ENTERTAINMENT_FILTERS),
      switchMap(() => {
        const filterArray: IFilter[] = [
          { id: 'attraction', name: this.translationService.instant('SideFilterComponent:TitleAttraction') },
          { id: 'comedy', name: this.translationService.instant('SideFilterComponent:TitleComedy') },
          { id: 'music', name: this.translationService.instant('SideFilterComponent:TitleMusic') },
          { id: 'sport', name: this.translationService.instant('SideFilterComponent:TitleSports') },
          { id: 'theatre', name: this.translationService.instant('SideFilterComponent:TitleTheatre') },
        ].sort((a, b) => {
          return a.name.localeCompare(b.name);
        });
        return of(new FilterActions.GetEntertainmentFiltersSuccess(filterArray));
      })
    )
  );

  public getMemberBenefitFilters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterActions.GET_MEMBER_BENEFITS_FILTERS),
      switchMap(() => {
        let tagParams = new HttpParams();
        tagParams = tagParams.append('module', 'member_benefits');
        tagParams = tagParams.append('tag_group', 'category');
        tagParams = tagParams.append('tag_group', 'esg');
        tagParams = tagParams.append('page_size', '200');
        return this.http.get(ApiEndpoint.TAGS, { params: tagParams }).pipe(
          map((result: PaginatedApiResponse<IFilterTags>) => {
            return new FilterActions.GetMemberBenefitsFiltersSuccess(result['results']);
          })
        );
      })
    )
  );

  public getArticleFilters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterActions.GET_ARTICLE_FILTERS),
      switchMap(() => {
        let tagParams = new HttpParams();
        tagParams = tagParams.append('module', 'articles');
        tagParams = tagParams.append('tag_group', 'article_category');
        tagParams = tagParams.append('page_size', '200');
        return this.http.get(ApiEndpoint.TAGS, { params: tagParams }).pipe(
          map((result: PaginatedApiResponse<IFilterTags>) => {
            return new FilterActions.GetArticleFiltersSuccess(result['results']);
          })
        );
      })
    )
  );

  public getInspirationFilters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterActions.GET_INSPIRATION_FILTERS),
      switchMap((action: FilterActions.GetInspirationFilters) => {
        const featuredCategoryUrl$ = this.http.get<PaginatedApiResponse<IFilterTags>>(ApiEndpoint.TAGS, {
          params: { module: 'inspiration', tag_group: 'featured_category', page_size: '20' },
        });

        const destinationUrl$ = this.http.get<Array<InternationalRegion>>(ApiEndpoint.INTERNATIONAL_REGIONS, {
          params: { ...action.payload },
        });

        return combineLatest([featuredCategoryUrl$, destinationUrl$]).pipe(
          map(([categories, destinations]) => {
            return new FilterActions.GetInspirationFiltersSuccess({
              category: categories['results'],
              destination: destinations,
            });
          })
        );
      })
    )
  );

  public getHotelFilters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterActions.GET_HOTEL_FILTERS),
      switchMap(() => {
        const url = replaceTemplateKeys(ApiEndpoint.TRAVEL_HOTEL_AMENITIES, {
          locale: this.languageService.currentLanguage,
        });

        const starRatingArray: IFilter[] = [
          { id: 3.0, name: this.translationService.instant('SideFilterComponent:TitleThreeStar') },
          { id: 4.0, name: this.translationService.instant('SideFilterComponent:TitleFourStar') },
          { id: 5.0, name: this.translationService.instant('SideFilterComponent:TitleFiveStar') },
        ];

        const startRatingResults$ = of(starRatingArray);
        const amenitiesResults$ = this.http.get(url).pipe(catchError(() => of({ amenities: [] })));

        return combineLatest([amenitiesResults$, startRatingResults$]).pipe(
          map((result: [any, any]) => {
            return new FilterActions.GetHotelFiltersSuccess({ amenities: result[0].amenities, starRatings: result[1] });
          })
        );
      })
    )
  );

  public getFlightFilters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterActions.GET_FLIGHT_FILTERS),
      map((action: FilterActions.GetFlightFilters) => {
        const times: IFilter[] = [
          {
            id: 'morning',
            value: DepartureTimeEnum.MORNING,
            name: this.translationService.instant('SideFilterComponent:TitleMorning'),
          },
          {
            id: 'afternoon',
            value: DepartureTimeEnum.AFTERNOON,
            name: this.translationService.instant('SideFilterComponent:TitleAfternoon'),
          },
          {
            id: 'evening',
            value: DepartureTimeEnum.EVENING,
            name: this.translationService.instant('SideFilterComponent:TitleEvening'),
          },
        ];

        return new FilterActions.GetFlightFiltersSuccess({ departure: times, return: times, airlines: action.payload });
      })
    )
  );

  public getCarFilters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterActions.GET_CAR_FILTERS),
      switchMap((action: FilterActions.GetCarFilters) => {
        const currentFilters = { ...action.currentFilters };
        let capacities = action.payload.pax_capacity;
        let suppliers = action.payload.supplier;

        if (Object.keys(currentFilters).length > 0 && currentFilters.pax_capacity) {
          currentFilters.pax_capacity = parseInt(currentFilters.pax_capacity.toString());
        }

        if (Object.keys(currentFilters).length > 0) {
          if (Object.keys(currentFilters).length === 1 && currentFilters.pax_capacity) {
            capacities = action.payload.pax_capacity;
          } else {
            const filters = { ...currentFilters, pax_capacity: null } as TravelCarHireFilterForm;
            const visibleCars = currentFilters.pax_capacity
              ? getFilteredCarResult(action.allCars, filters)
              : action.visibleCars;
            capacities = action.payload.pax_capacity.filter((option) =>
              visibleCars.some((car) => car.pax_capacity === option.id)
            );
          }

          if (Object.keys(currentFilters).length === 1 && currentFilters.supplier) {
            suppliers = action.payload.supplier;
          } else {
            const filters = { ...currentFilters, supplier: null } as TravelCarHireFilterForm;
            const hasSupplierFilter = !!currentFilters.supplier;
            const visibleCars = hasSupplierFilter ? getFilteredCarResult(action.allCars, filters) : action.visibleCars;

            suppliers = action.payload.supplier.filter((option) =>
              visibleCars.some((car) => {
                return capitalize(car.supplier_name) === capitalize(option.id);
              })
            );
          }
        }

        return of(
          new FilterActions.GetCarFiltersSuccess({
            pax_capacity: capacities,
            supplier: suppliers,
            car_type: action.payload.car_type,
            instalment_payment_available: action.visibleCars.some((r) => r.instalment_payments_available),
          })
        );
      })
    )
  );

  public getPreferencesFilters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterActions.GET_PREFERENCES_FILTERS),
      switchMap(() => {
        const filterArray: IFilter[] = [
          { id: 'attraction', name: this.translationService.instant('SideFilterComponent:TitleAttraction') },
          { id: 'comedy', name: this.translationService.instant('SideFilterComponent:TitleComedy') },
          { id: 'general', name: this.translationService.instant('Preferences:GeneralTag') },
          { id: 'music', name: this.translationService.instant('SideFilterComponent:TitleMusic') },
          { id: 'sport', name: this.translationService.instant('SideFilterComponent:TitleSports') },
          { id: 'theatre', name: this.translationService.instant('SideFilterComponent:TitleTheatre') },
          { id: 'travel', name: this.translationService.instant('Preferences:TravelTag') },
        ].sort((a, b) => {
          return a.name.localeCompare(b.name);
        });
        return of(new FilterActions.GetPrefrencesFiltersSuccess(filterArray));
      })
    )
  );
}
